import React, { Suspense, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import { SnackbarProvider, useSnackbar } from 'notistack';
import CancelIcon from '@mui/icons-material/Cancel';
import { CircularProgress, IconButton } from '@mui/material';

import JumboApp from '@jumbo/components/JumboApp';
import JumboDialog from '@jumbo/components/JumboDialog';
import JumboDialogProvider from '@jumbo/components/JumboDialog/JumboDialogProvider';
import JumboTheme from '@jumbo/components/JumboTheme';
import JumboRTL from '@jumbo/JumboRTL/JumboRTL';
import Div from '@jumbo/shared/Div';
import { DASHBOARD, LOGIN, USER } from '@jumbo/utils/constants/routes';
import axiosUtil from './services/axios';
import AppLayout from './AppLayout';
import AppRoutes from './AppRoutes';
import AppProvider from './AppProvider';
import { config } from './config/main';
import { getStoreList } from './redux/slice/storeSlice';
import { getDashboardList } from './redux/slice/dashboardSlice';

function SnackbarCloseButton({ snackbarKey }) {
  const { closeSnackbar } = useSnackbar();
  return (
    <IconButton onClick={() => closeSnackbar(snackbarKey)}>
      <CancelIcon sx={{ color: '#ffffff' }} />
    </IconButton>
  );
}

function App() {
  const token = useSelector((state) => state?.auth?.token);
  const user = useSelector((state) => state?.auth?.loggedUser);
  const { pathname } = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    axiosUtil.initalise();
  }, []);

  useEffect(() => {
    if (user?._id && user?.roleType !== 1) {
      dispatch(getStoreList({ data: {}, id: user?._id }))
        .unwrap()
        .then((response) => {
          if (response.status === 200) {
            const savedStoreId = localStorage.getItem('selectedStoreId');
            if (savedStoreId) {
              const savedStore = response?.data?.data?.store_list.find((store) => store._id === savedStoreId);
              if (savedStore) {
                dispatch(getDashboardList({ selectedStoreId: savedStore?._id }));
                return;
              }
            }
            const defaultStore = response?.data?.data?.store_list?.find((store) => store.isDefault === true);
            if (defaultStore) {
              localStorage.setItem('selectedStoreId', defaultStore?._id);
              dispatch(getDashboardList({ selectedStoreId: defaultStore?._id }));
            }
          }
        });
    }
  }, [user]);

  if (token) {
    if (pathname === LOGIN) {
      if (user?.roleType === 1) {
        return <Navigate to={USER} />;
      } else {
        return <Navigate to={DASHBOARD} />;
      }
    }
    if (user?.roleType === 1 && pathname === DASHBOARD) {
      return <Navigate to={USER} />;
    }
  }

  return (
    <AppProvider>
      <JumboApp activeLayout={config.defaultLayout}>
        <JumboTheme init={config.theme}>
          <JumboRTL>
            <JumboDialogProvider>
              <JumboDialog />
              <SnackbarProvider
                hideIconVariant
                action={(snackbarKey) => <SnackbarCloseButton snackbarKey={snackbarKey} />}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                maxSnack={3}
                autoHideDuration={4000}>
                <AppLayout>
                  <Suspense
                    fallback={
                      <Div
                        sx={{
                          display: 'flex',
                          minWidth: 0,
                          alignItems: 'center',
                          alignContent: 'center',
                          height: '100%',
                        }}>
                        <CircularProgress sx={{ m: '-40px auto 0' }} />
                      </Div>
                    }>
                    <AppRoutes />
                  </Suspense>
                </AppLayout>
              </SnackbarProvider>
            </JumboDialogProvider>
          </JumboRTL>
        </JumboTheme>
      </JumboApp>
    </AppProvider>
  );
}

export default App;
